import { useCallback, useEffect, useState } from 'react'

import { explain } from '@/shared/api/problem'
import { useCan } from '@/shared/auth/session'
import { fmtDateTime } from '@/shared/lib/format'
import { ErrorStrip, LoadingBlock, Note } from '@/shared/ui/states'

import { CELL_CONSENTS, memberConsents, recordMemberConsent, type VoterConsent } from './api'

/**
 * What one cell member has agreed to, and the switches that change it.
 *
 * ## Why this exists at all
 *
 * Consent was captured once, at enrolment, as a single boolean on the enrol call — and
 * then never again from any screen. The endpoints have always been there
 * (`GET`/`POST /voters/{id}/consents`, `voter:consent`); `api.ts` has had `voterConsents`
 * and `recordConsent` sitting unused since the voters feature was written. Nothing called
 * them, so a member who agreed to be rung the day AFTER they joined could not be recorded
 * as having agreed, and the coordinator's Call button refused them forever.
 *
 * That is what made the field app say "has asked not to be called" about people nobody
 * had ever asked: `canContact` needs a granted `CONTACT_CALL` row and there was no way in
 * the product to create one.
 *
 * ## Append-only, in both directions
 *
 * A withdrawal is a new row with `granted: false`, never an edit or a delete. So the
 * switches here POST rather than PATCH, the history stays intact, and "they agreed in
 * March and withdrew in July" remains a readable sequence rather than a current value
 * that lost its past.
 *
 * The CURRENT answer is the newest row per purpose, which is the same rule the server
 * applies in `VoterContactService.canContact` — stated once here so the screen and the
 * gate cannot disagree about what somebody has agreed to.
 */
export function MemberConsentPanel({ voterId, name }: { voterId: string; name: string }) {
  const canRecord = useCan('voter:consent')

  const [rows, setRows] = useState<VoterConsent[] | null>(null)
  const [busy, setBusy] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(async () => {
    setError(null)
    try {
      setRows(await memberConsents(voterId))
    } catch (e) {
      setError(explain(e))
      setRows([])
    }
  }, [voterId])

  useEffect(() => { void load() }, [load])

  /** Newest row wins — the same rule the server's contact gate applies. */
  const currentlyGranted = (purpose: string): VoterConsent | null => {
    if (!rows) return null
    const forPurpose = rows
      .filter((r) => r.purpose === purpose)
      .sort((a, b) => b.capturedAt.localeCompare(a.capturedAt))
    return forPurpose[0] ?? null
  }

  const toggle = async (purpose: string, granted: boolean) => {
    setBusy(purpose)
    setError(null)
    try {
      await recordMemberConsent(voterId, purpose, granted)
      await load()
    } catch (e) {
      setError(explain(e))
    } finally {
      setBusy(null)
    }
  }

  if (rows === null) return <LoadingBlock label="Loading consent…" />

  return (
    <div className="space-y-2">
      {error && <ErrorStrip message={error} />}

      <ul className="space-y-1">
        {CELL_CONSENTS.map(({ code, label }) => {
          const latest = currentlyGranted(code)
          const granted = latest?.granted ?? false
          return (
            <li key={code} className="flex items-center justify-between gap-3 text-erp-xs">
              <span className="min-w-0">
                <span className="block">{label}</span>
                <span className="block text-muted-foreground">
                  {latest
                    ? `${granted ? 'Agreed' : 'Withdrawn'} ${fmtDateTime(latest.capturedAt)}`
                    /* Not "refused". Nobody has put the question to them. */
                    : 'Never asked'}
                </span>
              </span>
              <button type="button"
                className={granted ? 'erp-btn-ghost h-7 text-erp-xs text-red-700'
                  : 'erp-btn-ghost h-7 text-erp-xs'}
                disabled={!canRecord || busy !== null}
                onClick={() => { void toggle(code, !granted) }}>
                {busy === code ? 'Saving…' : granted ? 'Withdraw' : 'Record consent'}
              </button>
            </li>
          )
        })}
      </ul>

      <Note>
        Only record what {name} has actually been asked and has actually said. Every entry
        is kept — withdrawing does not erase the agreement, it records the change.
      </Note>
    </div>
  )
}
